import React, { useRef, useState } from 'react';
import { FileUp, Loader, X } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export interface PreselectedOrder {
  orderNo: number;
  orderNoERP: string;
  supplierName: string | null;
}

interface DocUploadFormProps {
  /** Bestillinger valgt i tabellen; dokumentet tolkes mot disse (punkt 5). */
  preselectedOrders: PreselectedOrder[];
  enabled: boolean;
  busy: boolean;
  onSubmit: (file: File, documentType: 'Invoice' | 'PackingSlip' | undefined, orderNos: number[]) => void;
}

/**
 * Opplasting av leverandørdokument (TW 36423840, punkt 4–6): PDF, dokumenttype (gjenkjennes
 * eller velges) og valgfrie bestillinger. Uten bestillinger finner Compass dem selv.
 */
export const DocUploadForm: React.FC<DocUploadFormProps> = ({ preselectedOrders, enabled, busy, onSubmit }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<'' | 'Invoice' | 'PackingSlip'>('');
  const [orders, setOrders] = useState<PreselectedOrder[]>(preselectedOrders);

  const prefix = 'manager.modules.workingOrders.docInterpretation.upload';

  if (!enabled) {
    return (
      <div className="m-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        {t(`${prefix}.notEnabled`, {}, 'Dokumenttolking er ikke aktivert for denne kunden. Ta kontakt med IntegrasjonsPartner.')}
      </div>
    );
  }

  return (
    <div className="max-w-2xl m-6 p-6 bg-white border border-gray-200 rounded-lg space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {t(`${prefix}.file`, {}, 'Leverandørdokument (PDF)')}
        </label>
        <div
          className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
          onClick={() => inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) setFile(dropped);
          }}
        >
          <FileUp size={24} className="text-gray-400" />
          <span className="text-sm text-gray-600">
            {file ? file.name : t(`${prefix}.chooseFile`, {}, 'Velg eller dra inn en PDF')}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={e => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      <div>
        <label htmlFor="doc-type" className="block text-sm font-semibold text-gray-700 mb-2">
          {t(`${prefix}.documentType`, {}, 'Dokumenttype')}
        </label>
        <select
          id="doc-type"
          value={documentType}
          onChange={e => setDocumentType(e.target.value as '' | 'Invoice' | 'PackingSlip')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
        >
          <option value="">{t(`${prefix}.documentTypeAuto`, {}, 'Gjenkjennes automatisk')}</option>
          <option value="Invoice">{t(`${prefix}.documentTypeInvoice`, {}, 'Faktura')}</option>
          <option value="PackingSlip">{t(`${prefix}.documentTypePackingSlip`, {}, 'Pakkseddel')}</option>
        </select>
      </div>

      <div>
        <div className="block text-sm font-semibold text-gray-700 mb-2">
          {t(`${prefix}.orders`, {}, 'Bestillinger (valgfritt)')}
        </div>
        {orders.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t(`${prefix}.ordersEmpty`, {}, 'Ingen valgt. Compass finner bestillingene ut fra dokumentet. Velg bestillinger i tabellen før du åpner dette bildet for å tolke mot bestemte bestillinger.')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {orders.map(order => (
              <span key={order.orderNo} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-sm">
                {order.orderNoERP || order.orderNo}
                {order.supplierName && <span className="text-blue-600">· {order.supplierName}</span>}
                <button
                  type="button"
                  onClick={() => setOrders(orders.filter(o => o.orderNo !== order.orderNo))}
                  className="ml-1 p-1 rounded-full hover:bg-blue-100"
                  aria-label={t(`${prefix}.removeOrder`, { '0': order.orderNoERP }, `Fjern ${order.orderNoERP}`)}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={!file || busy}
        onClick={() => file && onSubmit(file, documentType || undefined, orders.map(o => o.orderNo))}
        className={`w-full px-4 py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${
          !file || busy ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {busy ? <Loader size={16} className="animate-spin" /> : <FileUp size={16} />}
        {busy
          ? t(`${prefix}.interpreting`, {}, 'Tolker dokumentet… det kan ta opptil ett minutt')
          : t(`${prefix}.submit`, {}, 'Les inn og tolk')}
      </button>
    </div>
  );
};
